import React, { useState, useEffect } from 'react';
import ComputerPlayer from './ComputerPlayer.js';
import { XIcon, OIcon } from './Icons.js';

// Main TicTacToe Component
const TicTacToe = () => {
  const [gameStarted, setGameStarted] = useState(false);
  const [playerTypes, setPlayerTypes] = useState({ X: 'human', O: 'human' });
  const [variant, setVariant] = useState('traditional'); // 'traditional' or 'misere'
  const [board, setBoard] = useState(Array(9).fill(null));
  const [isXNext, setIsXNext] = useState(true);
  const [winner, setWinner] = useState(null);
  const [computerPlayer] = useState(new ComputerPlayer());

  // Calculate winner - supports both traditional and misère variants
  const calculateWinner = (squares) => {
    const lines = [
      [0, 1, 2],
      [3, 4, 5],
      [6, 7, 8],
      [0, 3, 6],
      [1, 4, 7],
      [2, 5, 8],
      [0, 4, 8],
      [2, 4, 6],
    ];

    for (let i = 0; i < lines.length; i++) {
      const [a, b, c] = lines[i];
      if (squares[a] && squares[a] === squares[b] && squares[a] === squares[c]) {
        // In misère variant, making three in a row means you lose
        if (variant === 'misere') {
          // Return the opposite player (the one who didn't make three in a row wins)
          return squares[a] === 'X' ? 'O' : 'X';
        }
        // Traditional variant: return the player who made three in a row
        return squares[a];
      }
    }
    return null;
  };

  // Handle cell click
  const handleClick = (index) => {
    if (board[index] || winner) {
      return;
    }

    const newBoard = [...board];
    newBoard[index] = isXNext ? 'X' : 'O';
    setBoard(newBoard);

    const gameWinner = calculateWinner(newBoard);
    if (gameWinner) {
      setWinner(gameWinner);
    }

    setIsXNext(!isXNext);
  };

  // Reset game
  const resetGame = () => {
    setBoard(Array(9).fill(null));
    setIsXNext(true);
    setWinner(null);
    setGameStarted(false);
  };

  // Start game with selected player types and variant
  const startGame = () => {
    setGameStarted(true);
  };

  // Handle player type selection
  const handlePlayerTypeChange = (player, type) => {
    setPlayerTypes(prev => ({ ...prev, [player]: type }));
  };

  // Handle computer player moves
  useEffect(() => {
    if (!gameStarted || winner || isBoardFull) return;

    const currentPlayer = isXNext ? 'X' : 'O';
    const isComputerTurn = playerTypes[currentPlayer] === 'computer';

    if (isComputerTurn) {
      // Trigger computer move after a short delay for better UX
      const timeoutId = setTimeout(() => {
        const move = computerPlayer.makeMove(board, currentPlayer);
        if (move !== null) {
          handleClick(move);
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isXNext, gameStarted, winner, board, playerTypes]);

  // Check if board is full (draw)
  const isBoardFull = board.every(cell => cell !== null);

  // Render cell content
  const renderCell = (index) => {
    if (board[index] === 'X') {
      return <XIcon />;
    } else if (board[index] === 'O') {
      return <OIcon />;
    }
    return null;
  };

  // Get game status message
  const getStatus = () => {
    if (winner) {
      return `Vencedor: ${winner}`;
    } else if (isBoardFull) {
      return "Empate!";
    } else {
      return `Próximo Jogador: ${isXNext ? 'X' : 'O'}`;
    }
  };

  // Show player configuration screen before game starts
  if (!gameStarted) {
    return (
      <div className="game-container">
        <h1 className="game-title">Jogo do Galo</h1>
        <h2 className="config-subtitle">Configuração do Jogo</h2>
        
        {/* Variant Selection */}
        <div className="player-config">
          <div className="player-config-item">
            <h3>Variante do Jogo</h3>
            <div className="player-type-selector">
              <button
                className={`player-type-button ${variant === 'traditional' ? 'selected' : ''}`}
                onClick={() => setVariant('traditional')}
              >
                Tradicional
              </button>
              <button
                className={`player-type-button ${variant === 'misere' ? 'selected' : ''}`}
                onClick={() => setVariant('misere')}
              >
                Misère
              </button>
            </div>
            {variant === 'misere' && (
              <p className="variant-description">
                Na variante Misère, o jogador que fizer três em linha <strong>perde</strong>!
              </p>
            )}
          </div>
        </div>

        <h2 className="config-subtitle">Configuração dos Jogadores</h2>
        <div className="player-config">
          <div className="player-config-item">
            <h3>Jogador X</h3>
            <div className="player-type-selector">
              <button
                className={`player-type-button ${playerTypes.X === 'human' ? 'selected' : ''}`}
                onClick={() => handlePlayerTypeChange('X', 'human')}
              >
                Humano
              </button>
              <button
                className={`player-type-button ${playerTypes.X === 'computer' ? 'selected' : ''}`}
                onClick={() => handlePlayerTypeChange('X', 'computer')}
              >
                Computador
              </button>
            </div>
          </div>
          <div className="player-config-item">
            <h3>Jogador O</h3>
            <div className="player-type-selector">
              <button
                className={`player-type-button ${playerTypes.O === 'human' ? 'selected' : ''}`}
                onClick={() => handlePlayerTypeChange('O', 'human')}
              >
                Humano
              </button>
              <button
                className={`player-type-button ${playerTypes.O === 'computer' ? 'selected' : ''}`}
                onClick={() => handlePlayerTypeChange('O', 'computer')}
              >
                Computador
              </button>
            </div>
          </div>
        </div>
        <button className="new-game-button" onClick={startGame}>
          Iniciar Jogo
        </button>
      </div>
    );
  }

  return (
    <div className="game-container">
      <h1 className="game-title">Jogo do Galo</h1>
      <div className="variant-label">{variant === 'misere' ? 'Variante Misère' : 'Tradicional'}</div>
      <div className={`game-status ${winner ? 'winner' : ''}`}>
        {getStatus()}
      </div>
      <div className="game-board">
        {board.map((cell, index) => (
          <button
            key={index}
            className="cell"
            onClick={() => handleClick(index)}
            disabled={cell !== null || winner !== null}
          >
            {renderCell(index)}
          </button>
        ))}
      </div>
      <button className="new-game-button" onClick={resetGame}>
        Novo Jogo
      </button>
    </div>
  );
};

export default TicTacToe;
